import React, { useState, useMemo } from 'react';
import './PropertyList.css';

const PropertyList = ({
  properties,
  opportunityScores,
  onPropertySelect,
  selectedProperty,
  pagination,      // Optional: external pagination state { page, pageSize, total, totalPages }
  onPageChange,    // Optional: callback for page changes
  onSortChange,    // Optional: callback for sort changes (field, order)
  onSearch,        // Optional: callback for search (term)
  loading          // Optional: loading state
}) => {
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Internal pagination state (fallback)
  const [internalPage, setInternalPage] = useState(1);
  const itemsPerPage = 25;

  const isServerSide = !!pagination;
  const currentPage = isServerSide ? pagination.page : internalPage;

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (onSearch) {
      onSearch(term);
    }
  };

  // Define helper functions BEFORE they're used
  const calculateOpportunityScore = (property) => {
    let score = 0;
    if (property.vacancyPercent > 10) score += 20;
    if (property.yearBuilt < 1990) score += 15;
    if (property.rentPerSF < 1.5) score += 25;
    if (property.numberOfUnits > 100) score += 20;
    if (property.buildingClass === 'B' || property.buildingClass === 'C') score += 20;
    return score;
  };

  const calculateCapRate = (property) => {
    if (!property.rentPerSF || !property.rba) return 0;
    const annualRent = property.rentPerSF * property.rba;
    const estimatedValue = property.numberOfUnits * 75000;
    const noi = annualRent * 0.6;
    return (noi / estimatedValue) * 100;
  };

  const calculateRentGap = (property) => {
    const marketAvg = 1.8;
    if (!property.rentPerSF) return 0;
    return ((marketAvg - property.rentPerSF) / marketAvg) * 100;
  };

  // Filter and sort properties
  const processedProperties = useMemo(() => {
    // If server-side, properties are already the current page (and possibly filtered by search on backend)
    // We trust the backend for sorting if isServerSide is true.
    if (isServerSide) {
      // Just map calculated fields
      return (properties || []).map(property => {
        const score = opportunityScores?.find(s => s.propertyId === property.id);
        return {
          ...property,
          opportunityScore: score?.score || calculateOpportunityScore(property),
          capRate: calculateCapRate(property),
          rentGap: calculateRentGap(property)
        };
      });
    }

    let filtered = properties || [];

    // Client-side search
    filtered = filtered.filter(property => {
      if (!searchTerm) return true;

      const search = searchTerm.toLowerCase();
      return (
        property.propertyName?.toLowerCase().includes(search) ||
        property.propertyAddress?.toLowerCase().includes(search) ||
        property.city?.toLowerCase().includes(search) ||
        property.ownerName?.toLowerCase().includes(search)
      );
    });

    // Add opportunity scores
    filtered = filtered.map(property => {
      const score = opportunityScores?.find(s => s.propertyId === property.id);
      return {
        ...property,
        opportunityScore: score?.score || calculateOpportunityScore(property),
        capRate: calculateCapRate(property),
        rentGap: calculateRentGap(property)
      };
    });

    // Client-side Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (aVal === null || aVal === undefined) aVal = 0;
      if (bVal === null || bVal === undefined) bVal = 0;

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [properties, searchTerm, sortBy, sortOrder, opportunityScores, isServerSide]);

  // Pagination logic
  const displayProperties = useMemo(() => {
    if (isServerSide) {
      return processedProperties; // The array passed IS the page
    } else {
      const startIndex = (currentPage - 1) * itemsPerPage;
      return processedProperties.slice(startIndex, startIndex + itemsPerPage);
    }
  }, [processedProperties, currentPage, isServerSide]);

  const totalPages = isServerSide ? pagination.totalPages : Math.ceil(processedProperties.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (isServerSide && onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleSort = (field) => {
    const newOrder = (sortBy === field && sortOrder === 'asc') ? 'desc' : 'asc';

    // Update local state for UI indication
    setSortBy(field);
    setSortOrder(newOrder);

    // If server-side, trigger callback
    if (isServerSide && onSortChange) {
      onSortChange(field, newOrder);
    }
  };

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '—';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (!value || isNaN(value)) return '—';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const formatNumber = (value) => {
    if (!value) return '—';
    return value.toLocaleString();
  };

  return (
    <div className={`property-list ${loading ? 'loading' : ''}`}>
      <div className="list-header">
        <div className="list-controls">
          <div className="search-box">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
              <path d="M14 14L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search properties..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="list-stats">
            <span>
              {isServerSide
                ? `${pagination.total} properties found`
                : `${processedProperties.length} properties found`
              }
            </span>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="property-table">
          <thead>
            <tr>
              <th>
                <span>Score</span>
              </th>
              <th onClick={() => handleSort('propertyName')} className="sortable">
                <span>Property</span>
                {sortBy === 'propertyName' && (
                  <span className="sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th onClick={() => handleSort('numberOfUnits')} className="sortable">
                <span>Units</span>
                {sortBy === 'numberOfUnits' && (
                  <span className="sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th onClick={() => handleSort('buildingClass')} className="sortable">
                <span>Class</span>
                {sortBy === 'buildingClass' && (
                  <span className="sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th onClick={() => handleSort('yearBuilt')} className="sortable">
                <span>Year</span>
                {sortBy === 'yearBuilt' && (
                  <span className="sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th onClick={() => handleSort('rentPerSF')} className="sortable">
                <span>Rent/SF</span>
                {sortBy === 'rentPerSF' && (
                  <span className="sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th onClick={() => handleSort('vacancyPercent')} className="sortable">
                <span>Vacancy</span>
                {sortBy === 'vacancyPercent' && (
                  <span className="sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th onClick={() => handleSort('capRate')} className="sortable">
                <span>Cap Rate</span>
                {sortBy === 'capRate' && (
                  <span className="sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th onClick={() => handleSort('rentGap')} className="sortable">
                <span>Rent Gap</span>
                {sortBy === 'rentGap' && (
                  <span className="sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayProperties.map(property => (
              <tr
                key={property.id}
                className={selectedProperty?.id === property.id ? 'selected' : ''}
                onClick={() => onPropertySelect(property)}
              >
                <td>
                  <div className="score-badge" data-score={property.opportunityScore > 60 ? 'high' : property.opportunityScore > 30 ? 'medium' : 'low'}>
                    {property.opportunityScore}
                  </div>
                </td>
                <td>
                  <div className="property-info">
                    <div className="property-name">{property.propertyName || property.propertyAddress}</div>
                    <div className="property-address">{property.city}, {property.state}</div>
                  </div>
                </td>
                <td className="text-center">{formatNumber(property.numberOfUnits)}</td>
                <td>
                  <span className={`class-badge class-${(property.buildingClass || 'unknown').toLowerCase()}`}>
                    {property.buildingClass || '—'}
                  </span>
                </td>
                <td className="text-center">{property.yearBuilt || '—'}</td>
                <td className="text-right">{formatCurrency(property.rentPerSF)}</td>
                <td className="text-right">{formatPercent(property.vacancyPercent)}</td>
                <td className="text-right">{formatPercent(property.capRate)}</td>
                <td className="text-right">
                  <span className={property.rentGap > 0 ? 'positive' : 'negative'}>
                    {formatPercent(property.rentGap)}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPropertySelect(property);
                      }}
                      title="View Details"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="2" />
                        <path d="M2 8C2 8 4 3 8 3C12 3 14 8 14 8C14 8 12 13 8 13C4 13 2 8 2 8Z" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </button>
                    <button
                      className="btn-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Add to favorites or export single property
                      }}
                      title="Add to Portfolio"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2L10 6L14 7L11 10L12 14L8 12L4 14L5 10L2 7L6 6L8 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <div className="page-numbers">
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }

              return (
                <button
                  key={pageNum}
                  className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            className="page-btn"
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PropertyList;